import React, { useEffect, useRef, useState } from "react";
import { useBlocker } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { fetchQuestionTemplates } from "../Redux/feasibility";
import { sfqApi } from "../../../api/feasibility";
import TemplateListRail from "../TemplateListRail";
import QuestionRowsEditor, {
  toEditorRows, toQuestionPayload, findInvalidRow, invalidRowMessage,
} from "../QuestionRowsEditor";
import {
  TEMPLATE_NAME_MAX, templateNameError, templateErrorMessage,
} from "../templateName";
import { PERM } from "../../../utils/permissions";
import { hasAnyPermission, hasPermission } from "../../../utils/hasPermission";

const Wrap = styled.div`
  display: flex;
  align-items: flex-start;
`;

const Pane = styled.div`
  flex: 1;
  min-width: 0;
  padding-left: 20px;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const NameInput = styled.input`
  flex: 1;
  min-width: 0;
  max-width: 360px;
  padding: 5px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #212529;
  background: ${(p) => (p.readOnly ? "#f1f3f5" : "#fff")};
  outline: none;
  &:focus { border-color: #0d6efd; }
`;

const Btn = styled.button`
  padding: 5px 14px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid ${(p) => (p.$primary ? "#0d6efd" : p.$danger ? "#dc3545" : "#dee2e6")};
  background: ${(p) => (p.$primary ? "#0d6efd" : "#fff")};
  color: ${(p) => (p.$primary ? "#fff" : p.$danger ? "#dc3545" : "#495057")};
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { filter: brightness(0.9); }
`;

const Placeholder = styled.div`
  padding: 60px 0;
  text-align: center;
  color: #adb5bd;
  font-size: 13px;
`;

export default function QuestionTemplateManager({ onDirtyChange }) {
  const dispatch = useDispatch();
  const { questionTemplates, qtLoading } = useSelector((s) => s.feasibility);
  const permissions = useSelector((s) => s.userInfo.permissions);
  // 템플릿 쓰기(기본 템플릿 포함)는 백엔드 라우트가 SFQ_STUDY_EDIT 또는 SFQ_ADMIN을
  // 요구한다. 화면도 같은 조합으로 건다 — 탭을 여는 조건과도 동일하다.
  const canWriteTpl = hasAnyPermission(permissions, [PERM.SFQ_STUDY_EDIT, PERM.SFQ_ADMIN]);
  const canCreateUser = hasPermission(permissions, PERM.SFQ_STUDY_EDIT);

  const [selectedId, setSelectedId] = useState(null);
  const [set, setSet]         = useState(null);
  // 이름 입력칸의 편집 중인 값. set.name은 마지막으로 저장된 이름으로 남겨 둔다 —
  // 한 값을 같이 쓰면 타이핑이 set.name까지 바꿔 버려 blur 때 "바뀐 게 없다"가 되고
  // 이름 저장이 통째로 안 나갔다.
  const [nameDraft, setNameDraft] = useState("");
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);
  const rowRefs = useRef({});

  useEffect(() => {
    dispatch(fetchQuestionTemplates());
  }, [dispatch]);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (!selectedId) { setSet(null); setNameDraft(""); setRows([]); setDirty(false); return; }
    let cancelled = false;
    setLoading(true);
    sfqApi.getQuestionTemplate(selectedId)
      .then((res) => {
        if (cancelled) return;
        setSet(res.data.data.set);
        setNameDraft(res.data.data.set?.name || "");
        setRows(toEditorRows(res.data.data.questions));
        setDirty(false);
      })
      .catch(() => { if (!cancelled) { setSet(null); setNameDraft(""); setRows([]); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedId]);

  // 브라우저 새로고침·탭 닫기 가드
  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // 인앱 뒤로가기·메뉴 이동 가드
  const blocker = useBlocker(dirty);
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (window.confirm("저장하지 않은 변경 사항이 있습니다.\n페이지를 이동하면 변경 사항이 사라집니다.\n이동하시겠습니까?")) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker]);

  // 기본 템플릿도 이제 편집자·관리자 모두 고칠 수 있다. 목록에는 기본 템플릿과 내
  // 템플릿만 내려오므로, 쓰기 권한이 있으면 열려 있는 템플릿은 전부 편집 가능하다.
  const editable = !!set && canWriteTpl;

  function selectTemplate(id) {
    if (dirty && !window.confirm("저장하지 않은 변경 사항이 있습니다.\n다른 템플릿을 열면 변경 사항이 사라집니다.\n계속하시겠습니까?")) return;
    setSelectedId(id);
  }

  function handleRowsChange(next) {
    setRows(next);
    setDirty(true);
  }

  async function handleSave() {
    const invalid = findInvalidRow(rows);
    if (invalid) {
      alert(invalidRowMessage(invalid));
      const focusKey = invalid.field === "optionLabel"
        ? `${invalid.row._uid}_optionLabel_${invalid.optionUid}`
        : `${invalid.row._uid}_questionText`;
      rowRefs.current[focusKey]?.focus();
      return;
    }

    setSaving(true);
    try {
      // One transactional whole-set rewrite, not a create/update/delete per row.
      const res = await sfqApi.saveQuestionTemplateQuestions(
        selectedId, toQuestionPayload(rows, { includeActive: true })
      );
      setRows(toEditorRows(res.data.data));
      setDirty(false);
      dispatch(fetchQuestionTemplates());
    } catch (err) {
      alert("저장 실패: " + templateErrorMessage(err, "잠시 후 다시 시도해 주세요."));
    } finally {
      setSaving(false);
    }
  }

  async function handleCreate(scope) {
    const name = window.prompt(scope === "default" ? "새 기본 템플릿 이름" : "새 템플릿 이름");
    if (name === null) return;               // 취소
    const invalid = templateNameError(name);
    if (invalid) { if (name.trim()) alert(invalid); return; }
    try {
      const res = await sfqApi.createQuestionTemplate({ name: name.trim(), scope });
      await dispatch(fetchQuestionTemplates()).unwrap();
      setSelectedId(res.data.data.id);
    } catch (err) {
      alert("생성 실패: " + templateErrorMessage(err, "잠시 후 다시 시도해 주세요."));
    }
  }

  // 이름은 blur(또는 Enter)에서 커밋한다. 저장된 이름과 다를 때만 요청을 보내고,
  // 빈 이름이나 저장 실패는 서버가 들고 있는 이름으로 입력칸을 되돌린다.
  async function commitName() {
    if (!editable || !set) return;
    const name = nameDraft.trim();
    if (!name || name === set.name) { setNameDraft(set.name || ""); return; }
    const invalid = templateNameError(name);
    if (invalid) { alert(invalid); return; }   // 고칠 수 있게 입력값은 그대로 둔다
    try {
      await sfqApi.updateQuestionTemplate(selectedId, { name });
      setSet({ ...set, name });
      setNameDraft(name);
      dispatch(fetchQuestionTemplates());
    } catch (err) {
      alert("이름 변경 실패: " + templateErrorMessage(err, "잠시 후 다시 시도해 주세요."));
      setNameDraft(set.name || "");
    }
  }

  async function handleDelete() {
    if (!window.confirm(`"${set.name}" 템플릿과 그 문항을 모두 삭제하시겠습니까?`)) return;
    try {
      await sfqApi.deleteQuestionTemplate(selectedId);
      setSelectedId(null);
      dispatch(fetchQuestionTemplates());
    } catch (err) {
      alert("삭제 실패: " + templateErrorMessage(err, "잠시 후 다시 시도해 주세요."));
    }
  }

  // Read-only default template → give the user a private copy they can edit.
  async function handleClone() {
    const name = window.prompt("복제할 템플릿 이름", `${set.name} (복사본)`);
    if (name === null) return;               // 취소
    const invalid = templateNameError(name);
    if (invalid) { if (name.trim()) alert(invalid); return; }
    try {
      const res = await sfqApi.createQuestionTemplate({ name: name.trim(), scope: "user" });
      const newId = res.data.data.id;
      await sfqApi.saveQuestionTemplateQuestions(
        newId, toQuestionPayload(rows, { includeActive: true })
      );
      await dispatch(fetchQuestionTemplates()).unwrap();
      setSelectedId(newId);
    } catch (err) {
      alert("복제 실패: " + templateErrorMessage(err, "잠시 후 다시 시도해 주세요."));
    }
  }

  return (
    <Wrap>
      <TemplateListRail
        templates={questionTemplates}
        loading={qtLoading}
        selectedId={selectedId}
        onSelect={selectTemplate}
        onCreate={handleCreate}
        canCreateDefault={canWriteTpl}
        canCreateUser={canCreateUser}
        showCount
      />

      <Pane>
        {!selectedId ? (
          <Placeholder>왼쪽에서 템플릿을 선택하거나 새로 만드세요.</Placeholder>
        ) : loading || !set ? (
          <Placeholder>Loading...</Placeholder>
        ) : (
          <>
            <TopRow>
              <NameInput
                value={nameDraft}
                readOnly={!editable}
                maxLength={TEMPLATE_NAME_MAX}
                aria-label="템플릿 이름"
                onChange={(e) => setNameDraft(e.target.value)}
                onBlur={commitName}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              />
              {editable && (
                <Btn $primary disabled={!dirty || saving} onClick={handleSave}>
                  {saving ? "Saving..." : "Save Changes"}
                </Btn>
              )}
              {editable && !dirty && rows.length > 0 && (
                <span style={{ fontSize: 12, color: "#6c757d" }}>Saved</span>
              )}
              {/* 기본 템플릿을 직접 고치는 대신 내 사본으로 가져갈 때 쓴다. */}
              {canWriteTpl && <Btn onClick={handleClone}>내 템플릿으로 복제</Btn>}
              {editable && <Btn $danger onClick={handleDelete}>삭제</Btn>}
            </TopRow>

            <QuestionRowsEditor
              rows={rows}
              onChange={handleRowsChange}
              editable={editable}
              showActive
              rowRefs={rowRefs}
            />
          </>
        )}
      </Pane>
    </Wrap>
  );
}
